import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Search, Loader2, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { LEVEL_COLORS } from './ProcessTree';

const LEVEL_NAMES = { 1: "Enterprise", 2: "Value Stream", 3: "End-to-End Process", 4: "Sub-Process", 5: "Task" };

// Build breadcrumb path for a process
function getPath(proc, processMap) {
  const parts = [];
  let current = proc;
  while (current) {
    parts.unshift(current);
    current = current.parent_process_id ? processMap[current.parent_process_id] : null;
  }
  return parts;
}

const ProcessSelector = ({ value, onChange, label }) => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProc, setNewProc] = useState({ name: '', level: '4', parent_process_id: '', description: '' });

  useEffect(() => {
    api.get('/processes').then(r => setProcesses(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const processMap = useMemo(() => {
    const m = {};
    processes.forEach(p => { m[p.id] = p; });
    return m;
  }, [processes]);

  const selectedProc = processMap[value];

  const filtered = useMemo(() => {
    if (!search) return processes;
    const q = search.toLowerCase();
    return processes.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.tags?.some(t => t.toLowerCase().includes(q)) ||
      p.level_name?.toLowerCase().includes(q)
    );
  }, [processes, search]);

  const handleCreate = async () => {
    if (!newProc.name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      const res = await api.post('/processes', {
        name: newProc.name,
        level: parseInt(newProc.level),
        parent_process_id: newProc.parent_process_id || null,
        description: newProc.description,
        status: 'Draft',
        process_type: 'Core',
      });
      // Refresh processes list
      const updated = await api.get('/processes');
      setProcesses(updated.data);
      onChange(res.data.id, parseInt(newProc.level));
      setShowCreate(false);
      setNewProc({ name: '', level: '4', parent_process_id: '', description: '' });
      toast.success('Process created and linked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  // Auto-set level based on parent
  const handleParentChange = (parentId) => {
    setNewProc(f => {
      const parent = processMap[parentId];
      const newLevel = parent ? String(Math.min(parent.level + 1, 5)) : f.level;
      return { ...f, parent_process_id: parentId, level: newLevel };
    });
  };

  // Get eligible parents for selected level
  const eligibleParents = useMemo(() => {
    const lvl = parseInt(newProc.level);
    return processes.filter(p => p.level < lvl);
  }, [processes, newProc.level]);

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading processes...</div>;

  return (
    <div data-testid="process-selector">
      <Label className="text-sm font-medium">{label || 'Linked Process'}</Label>
      <div className="mt-1">
        {selectedProc ? (
          <div className="flex items-center gap-2 p-2 border rounded-lg bg-slate-50">
            {/* Show hierarchy path */}
            <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
              {getPath(selectedProc, processMap).map((p, i, arr) => (
                <span key={p.id} className="flex items-center gap-1 flex-shrink-0">
                  {i > 0 && <ChevronRight className="w-3 h-3 text-slate-300" />}
                  <span
                    className="w-4 h-4 rounded flex items-center justify-center text-white text-[10px] font-bold"
                    style={{ backgroundColor: LEVEL_COLORS[p.level] || '#94a3b8' }}
                  >{p.level}</span>
                  <span className={`text-sm ${i === arr.length - 1 ? 'font-semibold text-slate-900' : 'text-slate-400'}`}>
                    {p.name}
                  </span>
                </span>
              ))}
            </div>
            <button onClick={() => onChange(null, null)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search processes..." className="pl-9 h-9 text-sm" />
            </div>
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 p-3 text-center">No processes found</p>
              ) : (
                filtered.slice(0, 30).map(p => {
                  const path = getPath(p, processMap);
                  return (
                    <button key={p.id} className="w-full flex flex-col gap-0.5 p-2 hover:bg-slate-50 text-left border-b last:border-0" onClick={() => { onChange(p.id, p.level); setSearch(''); }}>
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: LEVEL_COLORS[p.level] || '#94a3b8' }}>{p.level}</span>
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        <span className="text-xs text-slate-400 ml-auto flex-shrink-0">{p.level_name || LEVEL_NAMES[p.level]}</span>
                      </div>
                      {path.length > 1 && (
                        <div className="flex items-center gap-1 ml-7 text-xs text-slate-400">
                          {path.slice(0, -1).map((anc, i) => (
                            <span key={anc.id} className="flex items-center gap-0.5">
                              {i > 0 && <ChevronRight className="w-2.5 h-2.5" />}
                              {anc.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCreate(true)} className="text-xs">
                <Plus className="w-3 h-3 mr-1" /> Create New Process
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onChange(null, null)} className="text-xs text-slate-400">
                Leave blank
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Process Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Process</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name *</Label><Input value={newProc.name} onChange={e => setNewProc(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Bank Reconciliation" /></div>
            <div><Label>Parent Process (where does it sit?)</Label>
              <Select value={newProc.parent_process_id || 'none'} onValueChange={v => handleParentChange(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select parent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (root)</SelectItem>
                  {processes.filter(p => p.level < 5).map(p => {
                    const path = getPath(p, processMap);
                    const pathStr = path.map(x => x.name).join(' > ');
                    return <SelectItem key={p.id} value={p.id}>L{p.level} {pathStr}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              {newProc.parent_process_id && processMap[newProc.parent_process_id] && (
                <p className="text-xs text-slate-500 mt-1">
                  Will be created as L{newProc.level} ({LEVEL_NAMES[parseInt(newProc.level)]}) under {processMap[newProc.parent_process_id].name}
                </p>
              )}
            </div>
            <div><Label>Level</Label>
              <Select value={newProc.level} onValueChange={v => setNewProc(f => ({ ...f, level: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{[1,2,3,4,5].map(l => <SelectItem key={l} value={String(l)}>L{l} - {LEVEL_NAMES[l]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={newProc.description} onChange={e => setNewProc(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#235894] hover:bg-[#1E4B80]">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Create & Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcessSelector;
